import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronLeft, ChevronRight, Loader2, BarChart3, X, CalendarDays, List,
  AlertTriangle, Users, UserCheck, CheckCircle2, PartyPopper, Phone,
  MessageCircle, Mail, Check, X as XIcon, Clock as ClockIcon, Star, QrCode,
} from "lucide-react";
import { eventosApi } from "../../lib/api";
import { LiveClock } from "../../components/LiveClock";
import { PageHeader } from "../../components/PageHeader";
import { reservasApi, sucursalesApi, encuestasApi, type EncuestaQrActiva } from "../../lib/api";
import { NuevaReservaModal } from "../../components/NuevaReservaModal";
import { EncuestaQrGrandeModal } from "../../components/EncuestaQrGrandeModal";
import { useInicio, puedeVerEncuestaQr } from "../../lib/permisos";
import { useAuth } from "../../lib/auth";

const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const DIAS_SEMANA = ["Lun","Mar","Mié","Jue","Vie","Sáb","Dom"];

const ESTADO_COLORS: Record<string, string> = {
  pendiente: "bg-amber-100 text-amber-800",
  confirmada: "bg-emerald-100 text-emerald-800",
  cancelada: "bg-rose-100 text-rose-800",
  no_show: "bg-slate-200 text-slate-700",
  atendida: "bg-blue-100 text-blue-800",
  en_mesa: "bg-indigo-100 text-indigo-800",
};
const ESTADO_LABELS: Record<string, string> = {
  pendiente: "Pendiente", confirmada: "Confirmada", cancelada: "Cancelada",
  no_show: "No-show", atendida: "Atendida", en_mesa: "En mesa",
};

const ES_VERDE = new Set(["confirmada", "atendida", "en_mesa"]);
const ES_AMBAR = new Set(["pendiente"]);
const ES_ROJO  = new Set(["cancelada", "no_show"]);

const ACCENT_PALETTE = ["#3A6B8C", "#9A6A3C", "#5E7C53", "#8C4A52", "#6B5B95", "#A05A2C"];
function accentFor(id: number) { return ACCENT_PALETTE[(id - 1) % ACCENT_PALETTE.length]; }

function hhmm(s?: string) { return s && s.length >= 5 ? s.slice(0, 5) : (s ?? ""); }

export default function CalendarioPage() {
  const inicio = useInicio();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sucParam = searchParams.get("sucursal");
  const sucInicial = sucParam ? Number(sucParam) : undefined;
  const hoy = new Date();
  const hoyISO = `${hoy.getFullYear()}-${String(hoy.getMonth()+1).padStart(2,"0")}-${String(hoy.getDate()).padStart(2,"0")}`;
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const [sucursal, setSucursal] = useState<number | undefined>(sucInicial);
  const [diaSel, setDiaSel] = useState<string | null>(hoyISO);
  const [modalNueva, setModalNueva] = useState(false);
  const [recarga, setRecarga] = useState(0);
  const [panelVencer, setPanelVencer] = useState(false);

  const { data: porVencerData, refetch: refetchVencer } = useQuery({
    queryKey: ["por-vencer", recarga],
    queryFn: () => reservasApi.porVencer(),
    refetchInterval: 60_000,
  });

  const { data: sucursales } = useQuery({
    queryKey: ["sucursales"],
    queryFn: () => sucursalesApi.list(),
  });
  const SUCURSAL_NOMBRE: Record<number, string> = Object.fromEntries(
    (sucursales ?? []).map((s) => [s.id, s.nombre]),
  );

  const primerDiaMes = `${anio}-${String(mes).padStart(2,"0")}-01`;
  const ultimoDiaMes = `${anio}-${String(mes).padStart(2,"0")}-${new Date(anio, mes, 0).getDate()}`;

  const { data: allData, isLoading } = useQuery({
    queryKey: ["calendario-reservas", anio, mes, recarga],
    queryFn: () => reservasApi.list({ desde: primerDiaMes, hasta: ultimoDiaMes, limit: 2000 }),
  });

  const { data: eventosData } = useQuery({
    queryKey: ["calendario-eventos", anio, mes, recarga],
    queryFn: () => eventosApi.list({ desde: primerDiaMes, hasta: ultimoDiaMes }),
  });

  const [eventoDetalle, setEventoDetalle] = useState<any | null>(null);
  const [encuestaQrAbierta, setEncuestaQrAbierta] = useState<EncuestaQrActiva | null>(null);

  const { data: encuestasQr } = useQuery({
    queryKey: ["encuestas-qr-activas"],
    queryFn: () => encuestasApi.listarActivasQr(),
    enabled: puedeVerEncuestaQr(user),
    refetchInterval: 60_000,
  });

  const eventosPorDia = useMemo(() => {
    const map = new Map<string, any[]>();
    for (const ev of (eventosData ?? [])) {
      const f = ev.fecha?.slice(0, 10);
      if (!f) continue;
      if (!map.has(f)) map.set(f, []);
      map.get(f)!.push(ev);
    }
    return map;
  }, [eventosData]);

  const reservas = useMemo(() => {
    const all = (allData?.items as any[]) ?? [];
    if (!sucursal) return all;
    return all.filter((r) => r.sucursalId === sucursal);
  }, [allData, sucursal]);

  const porDia = useMemo(() => {
    const map = new Map<string, { verdes: number; ambares: number; rojas: number; total: number; comensales: number }>();
    for (const r of reservas) {
      const f = r.fechaReserva;
      const e = map.get(f) ?? { verdes: 0, ambares: 0, rojas: 0, total: 0, comensales: 0 };
      e.total++;
      e.comensales += r.numPersonas ?? 0;
      if (ES_VERDE.has(r.estado)) e.verdes++;
      else if (ES_AMBAR.has(r.estado)) e.ambares++;
      else if (ES_ROJO.has(r.estado)) e.rojas++;
      map.set(f, e);
    }
    return map;
  }, [reservas]);

  const itemsDia = useMemo(() => {
    if (!diaSel) return [];
    return reservas
      .filter((r) => r.fechaReserva === diaSel)
      .sort((a, b) => hhmm(a.horaReserva).localeCompare(hhmm(b.horaReserva)));
  }, [reservas, diaSel]);

  const totalReservas = reservas.length;
  const totalComensales = reservas.reduce((s, r) => s + (r.numPersonas ?? 0), 0);
  const totalPendientes = reservas.filter((r) => r.estado === "pendiente").length;

  const primerDia = new Date(anio, mes - 1, 1);
  const ultimoDia = new Date(anio, mes, 0).getDate();
  const offset = (primerDia.getDay() + 6) % 7;
  const celdas: (number | null)[] = [];
  for (let i = 0; i < offset; i++) celdas.push(null);
  for (let d = 1; d <= ultimoDia; d++) celdas.push(d);
  while (celdas.length % 7 !== 0) celdas.push(null);

  function fechaISO(dia: number) { return `${anio}-${String(mes).padStart(2,"0")}-${String(dia).padStart(2,"0")}`; }
  function esHoy(dia: number) { return anio === hoy.getFullYear() && mes === hoy.getMonth()+1 && dia === hoy.getDate(); }
  function mesAnterior() { if (mes===1){setMes(12);setAnio(anio-1);}else setMes(mes-1); setDiaSel(null); }
  function mesSiguiente() { if (mes===12){setMes(1);setAnio(anio+1);}else setMes(mes+1); setDiaSel(null); }

  function fechaLegible(iso: string) {
    const d = new Date(iso + "T00:00:00");
    return d.toLocaleDateString("es-VE", { weekday: "long", day: "2-digit", month: "long" });
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Header de restaurante */}
      <PageHeader
        crumbs={[{ label: "Inicio", to: inicio }, { label: "Calendario" }]}
        icon={CalendarDays}
        actions={[
          ...(porVencerData?.count ?? 0) > 0 ? [{
            label: `${porVencerData!.count} por vencer`,
            icon: AlertTriangle as any,
            onClick: () => setPanelVencer(v => !v),
            variant: "secondary" as const,
            badge: porVencerData!.count,
          }] : [],
          { label: "Reportes", icon: BarChart3, to: "/reportes", variant: "ghost" as const },
          { label: "Reservas", icon: List, to: "/reservas", variant: "ghost" as const },
        ]}
      />

      <main className="flex-1 mx-auto max-w-7xl w-full px-6 py-6">
        {/* Controles: mes + sucursal + leyenda (sticky en scroll) */}
        <div className="sticky top-0 z-20 -mx-6 px-6 pt-1 pb-3 mb-4 bg-gradient-to-b from-slate-50 via-slate-50 to-slate-50/80 backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <button onClick={mesAnterior} aria-label="Mes anterior" className="size-11 rounded-lg border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50">
                <ChevronLeft className="size-5 text-slate-600" />
              </button>
              <h1 className="text-2xl font-bold text-slate-900 min-w-[200px] text-center capitalize">{MESES[mes-1]} <span className="text-slate-500">{anio}</span></h1>
              <button onClick={mesSiguiente} aria-label="Mes siguiente" className="size-11 rounded-lg border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50">
                <ChevronRight className="size-5 text-slate-600" />
              </button>
            </div>
            <div className="flex flex-wrap bg-white rounded-lg border border-slate-200 p-1">
              <button onClick={() => setSucursal(undefined)} className={`px-3 py-1.5 text-sm rounded-md font-medium ${!sucursal?"bg-slate-800 text-white":"text-slate-600 hover:bg-slate-50"}`}>Todas</button>
              {(sucursales ?? []).map((s) => (
                <button key={s.id} onClick={() => setSucursal(s.id)}
                  className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${sucursal===s.id?"text-white":"text-slate-600 hover:bg-slate-50"}`}
                  style={sucursal === s.id ? { backgroundColor: accentFor(s.id) } : {}}>
                  {s.nombre}
                </button>
              ))}
            </div>
          </div>
          {/* Leyenda permanente arriba */}
          <div className="flex items-center gap-4 mt-3 text-[11px] text-slate-600 font-medium">
            <span className="uppercase tracking-wider text-slate-400 text-[10px]">Leyenda:</span>
            <span className="inline-flex items-center gap-1.5"><span className="size-2.5 rounded-full bg-emerald-500" /> Confirmadas</span>
            <span className="inline-flex items-center gap-1.5"><span className="size-2.5 rounded-full bg-amber-400" /> Pendientes</span>
            <span className="inline-flex items-center gap-1.5"><span className="size-2.5 rounded-full bg-rose-400" /> Cancel./No-show</span>
            <span className="inline-flex items-center gap-1.5 ml-auto text-purple-600"><PartyPopper size={11} aria-hidden /> Evento</span>
          </div>
        </div>

        {/* Resumen del mes */}
        <div className="flex gap-3 mb-5">
          <div className="bg-white border border-slate-200 rounded-lg px-4 py-2.5">
            <span className="text-xl font-bold text-slate-800">{totalReservas}</span>
            <span className="text-sm text-slate-500 ml-1.5">reservas</span>
          </div>
          <div className="bg-white border border-slate-200 rounded-lg px-4 py-2.5">
            <span className="text-xl font-bold text-slate-800">{totalComensales}</span>
            <span className="text-sm text-slate-500 ml-1.5">comensales</span>
          </div>
          {totalPendientes > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
              <span className="text-xl font-bold text-amber-700">{totalPendientes}</span>
              <span className="text-sm text-amber-600 ml-1.5">pendientes</span>
            </div>
          )}
        </div>

        {/* Encuestas con QR activo: para que mesoneros/cajeros se las ofrezcan a los clientes */}
        {(encuestasQr?.length ?? 0) > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5">
            <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide flex items-center gap-1.5 mb-2">
              <Star className="size-3.5" /> Encuestas con QR activo — ofrecéselas a los clientes
            </p>
            <div className="flex flex-wrap gap-2">
              {encuestasQr!.map((e) => (
                <button key={e.id} onClick={() => setEncuestaQrAbierta(e)}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-amber-200 hover:border-amber-400 hover:shadow-sm transition-all text-sm">
                  <QrCode className="size-4 text-amber-600" />
                  <span className="font-medium text-slate-700">{e.titulo}</span>
                  {e.sucursalNombre && <span className="text-xs text-slate-400">· {e.sucursalNombre}</span>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Layout: calendario + panel del día */}
        <div className="flex flex-col lg:flex-row gap-4">

          {/* Grilla del calendario */}
          <div className={`bg-white border border-slate-200 rounded-xl p-4 ${diaSel ? "lg:flex-1" : "w-full"}`}>
            {isLoading ? (
              <div className="p-16 flex items-center justify-center text-slate-400"><Loader2 className="size-6 animate-spin mr-2" /> Cargando…</div>
            ) : (
              <>
                <div className="grid grid-cols-7 gap-1.5 mb-1.5">
                  {DIAS_SEMANA.map((d) => <div key={d} className="text-center text-[11px] font-semibold text-slate-400 uppercase py-1 tracking-wide">{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1.5">
                  {celdas.map((dia, i) => {
                    if (dia === null) return <div key={`e-${i}`} className="aspect-square" />;
                    const iso = fechaISO(dia);
                    const info = porDia.get(iso);
                    const tiene = info && info.total > 0;
                    const sel = diaSel === iso;
                    const evsDia = eventosPorDia.get(iso) ?? [];
                    return (
                      <button key={dia} onClick={() => setDiaSel(iso)}
                        className={`aspect-square rounded-xl border relative overflow-hidden flex flex-col justify-end transition-all
                          ${sel
                            ? "border-slate-800 ring-2 ring-slate-300 bg-slate-800"
                            : esHoy(dia)
                              ? "border-brand-400 bg-brand-50/40"
                              : tiene
                                ? "border-slate-200 bg-white hover:border-slate-400 hover:shadow-sm"
                                : "border-slate-100 bg-slate-50/50 hover:bg-white hover:border-slate-200"}`}>
                        {/* Número grande como marca de agua */}
                        <span className={`absolute -top-0.5 right-1 font-black leading-none select-none
                          ${sel
                            ? "text-[2.2rem] text-white/25"
                            : esHoy(dia)
                              ? "text-[2.2rem] text-brand-300"
                              : tiene
                                ? "text-[2.2rem] text-slate-200"
                                : "text-[2.2rem] text-slate-200/50"}`}>
                          {dia}
                        </span>
                        {/* Indicadores de estado */}
                        <div className="relative z-10 p-1.5 space-y-0.5">
                          {/* Eventos privados del día */}
                          {evsDia.map((ev: any) => (
                            <div key={ev.id}
                              className="flex items-center gap-0.5 bg-indigo-600 text-white text-[9px] font-bold px-1 py-0.5 rounded truncate">
                              <PartyPopper size={11} className="shrink-0 inline" aria-hidden /> <span className="truncate flex-1 min-w-0">{ev.nombre}</span>
                            </div>
                          ))}
                          {tiene ? (
                            <div className="flex items-center gap-0.5 flex-wrap">
                              {info!.verdes > 0 && (
                                <span className="bg-emerald-500 text-white text-[10px] font-bold min-w-[18px] text-center px-1 py-[1px] rounded-md shadow-sm">
                                  {info!.verdes}
                                </span>
                              )}
                              {info!.ambares > 0 && (
                                <span className="bg-amber-400 text-white text-[10px] font-bold min-w-[18px] text-center px-1 py-[1px] rounded-md shadow-sm">
                                  {info!.ambares}
                                </span>
                              )}
                              {info!.rojas > 0 && (
                                <span className="bg-rose-400 text-white text-[10px] font-bold min-w-[18px] text-center px-1 py-[1px] rounded-md shadow-sm">
                                  {info!.rojas}
                                </span>
                              )}
                            </div>
                          ) : (
                            esHoy(dia) ? <span className="text-[10px] text-brand-400 font-medium">hoy</span> : null
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {/* leyenda movida arriba (sticky) */}
              </>
            )}
          </div>

          {/* Panel lateral del día */}
          {diaSel && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 w-full lg:w-96 shrink-0">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h2 className="font-semibold text-slate-800 capitalize">{fechaLegible(diaSel)}</h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {itemsDia.length} reserva{itemsDia.length !== 1 ? "s" : ""}
                    {sucursal ? ` · ${SUCURSAL_NOMBRE[sucursal] ?? ""}` : " · Todos los locales"}
                  </p>
                </div>
                <button onClick={() => setDiaSel(null)} className="text-slate-400 hover:text-slate-700"><X className="size-5" /></button>
              </div>
              <button onClick={() => setModalNueva(true)}
                className="w-full mb-3 px-3 py-2 bg-slate-800 text-white text-sm rounded-lg hover:bg-slate-900 font-medium transition-colors">
                + Nueva reserva
              </button>

              {/* Eventos privados del día seleccionado */}
              {diaSel && (eventosPorDia.get(diaSel) ?? []).map((ev: any) => {
                const isOpen = eventoDetalle?.id === ev.id;
                const invitados = eventoDetalle?.invitados ?? [];
                const total = isOpen ? invitados.length : 0;
                const llegaron = isOpen ? invitados.filter((i: any) => i.estado === 'llegó').length : 0;
                return (
                  <div key={ev.id} className="mb-2 rounded-xl border-2 border-indigo-300 bg-indigo-50 overflow-hidden">
                    <button onClick={async () => {
                      if (isOpen) { setEventoDetalle(null); }
                      else { const d = await eventosApi.get(ev.id); setEventoDetalle(d); }
                    }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-indigo-100 transition-colors text-left">
                      <PartyPopper size={20} className="text-purple-600 shrink-0" aria-hidden />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-indigo-900 truncate">{ev.nombre}</p>
                        <p className="text-xs text-indigo-600">
                          {ev.horaInicio}{ev.horaFin ? ` – ${ev.horaFin}` : ''} · {ev.totalInvitados} personas
                          {isOpen && total > 0 && ` · ${llegaron}/${total} llegaron`}
                        </p>
                      </div>
                      <ChevronRight className={`size-4 text-indigo-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                    </button>
                    {isOpen && (
                      <div className="border-t border-indigo-200 max-h-48 overflow-y-auto">
                        {invitados.filter((i: any) => i.estado === 'pendiente').map((inv: any) => (
                          <div key={inv.id} className="flex items-center gap-2 px-3 py-2 bg-white hover:bg-slate-50 border-b border-slate-100">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
                            <span className="flex-1 text-xs font-medium text-slate-700 truncate">
                              {inv.nombre}
                              {(inv.acompanantes ?? 0) > 0 && <span className="text-indigo-400 ml-1">+{inv.acompanantes}</span>}
                            </span>
                            <button onClick={async () => { await eventosApi.checkIn(inv.id); const d = await eventosApi.get(ev.id); setEventoDetalle(d); setRecarga(r => r+1); }}
                              className="text-[10px] px-2 py-1 bg-emerald-600 text-white rounded font-semibold hover:bg-emerald-700 shrink-0 inline-flex items-center gap-1">
                              Llegó <Check size={11} aria-hidden />
                            </button>
                          </div>
                        ))}
                        {invitados.filter((i: any) => i.estado === 'llegó').map((inv: any) => (
                          <div key={inv.id} className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border-b border-emerald-100">
                            <CheckCircle2 className="size-3.5 text-emerald-500 shrink-0" />
                            <span className="flex-1 text-xs text-emerald-800 font-medium truncate">{inv.nombre}</span>
                            <span className="text-[10px] text-emerald-600">
                              {inv.llegadaAt && new Date(inv.llegadaAt).toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {isLoading ? (
                <div className="py-8 flex items-center justify-center text-slate-400"><Loader2 className="size-5 animate-spin mr-2" /> Cargando…</div>
              ) : itemsDia.length === 0 && (eventosPorDia.get(diaSel ?? '') ?? []).length === 0 ? (
                <div className="py-8 text-center text-slate-400 text-sm">Sin reservas ni eventos este día</div>
              ) : itemsDia.length === 0 ? null : (
                <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                  {itemsDia.map((r) => {
                    const showSuc = !sucursal && r.sucursalId;
                    return (
                      <button key={r.id} onClick={() => navigate(`/reservas/${r.id}`)}
                        className="w-full text-left rounded-lg p-3 hover:bg-slate-50 transition-colors border border-slate-200"
                        style={showSuc ? { borderLeftWidth: 3, borderLeftColor: accentFor(r.sucursalId) } : {}}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono text-sm font-semibold text-slate-800">{hhmm(r.horaReserva)}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${ESTADO_COLORS[r.estado] ?? ""}`}>{ESTADO_LABELS[r.estado] ?? r.estado}</span>
                        </div>
                        <div className="font-medium text-slate-800 text-sm">{r.nombreCliente}</div>
                        <div className="text-xs text-slate-500 flex flex-wrap gap-x-3 mt-0.5">
                          <span>{r.numPersonas} pers.</span>
                          {showSuc && <span style={{ color: accentFor(r.sucursalId) }} className="font-medium">{SUCURSAL_NOMBRE[r.sucursalId] ?? `#${r.sucursalId}`}</span>}
                          {r.telefono && <span>{r.telefono}</span>}
                        </div>
                        {r.requerimientosEspeciales && (
                          <div className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 mt-1.5 truncate">{r.requerimientosEspeciales}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {modalNueva && diaSel && (
          <NuevaReservaModal
            fechaInicial={diaSel}
            onClose={() => setModalNueva(false)}
            onCreated={() => setRecarga((r) => r + 1)}
          />
        )}

        {encuestaQrAbierta && (
          <EncuestaQrGrandeModal encuesta={encuestaQrAbierta} onClose={() => setEncuestaQrAbierta(null)} />
        )}
      </main>

      {/* Panel lateral: reservas por vencer */}
      {panelVencer && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setPanelVencer(false)} />
          <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col h-full">
            {/* Header del panel */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-amber-200 bg-amber-50">
              <div className="flex items-center gap-2">
                <AlertTriangle className="size-5 text-amber-600" />
                <h3 className="font-bold text-amber-800">Por vencer ({porVencerData?.count ?? 0})</h3>
              </div>
              <button onClick={() => setPanelVencer(false)} className="text-amber-600 hover:text-amber-800">
                <X className="size-5" />
              </button>
            </div>
            <p className="px-5 py-2 text-xs text-amber-700 bg-amber-50 border-b border-amber-100">
              Reservas pendientes que se cancelarán automáticamente en las próximas 24 horas.
            </p>

            {/* Lista */}
            <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
              {(porVencerData?.reservas ?? []).map((r: any) => {
                const waMsg = `Hola ${r.nombreCliente}! Te contactamos por tu reserva %23${r.id} en ${r.sucursalNombre} el ${r.fechaReserva} a las ${r.horaReserva}. ¿Confirmas tu asistencia?`;
                const venceAt = new Date(r.venceAt);
                const minutosRestantes = Math.max(0, Math.round((venceAt.getTime() - Date.now()) / 60000));
                const urgente = minutosRestantes < 120;
                return (
                  <div key={r.id} className={`px-5 py-4 ${urgente ? "bg-red-50" : "bg-white"}`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="font-semibold text-slate-800">{r.nombreCliente}</p>
                        <p className="text-xs text-slate-500">
                          {r.fechaReserva} · {r.horaReserva} · {r.numPersonas} pers.
                          {r.contexto ? ` · ${r.contexto}` : ""}
                        </p>
                        <p className="text-xs text-slate-400">{r.sucursalNombre}</p>
                      </div>
                      <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${urgente ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                        {minutosRestantes < 60
                          ? `${minutosRestantes}min`
                          : `${Math.round(minutosRestantes / 60)}h`}
                      </span>
                    </div>

                    {/* Contacto */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {r.telefono && (
                        <a href={`tel:${r.telefono}`}
                          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">
                          <Phone size={12} aria-hidden /> {r.telefono}
                        </a>
                      )}
                      {(r.whatsappNumero || r.telefono) && (
                        <a href={`https://wa.me/${(r.whatsappNumero || r.telefono)?.replace(/\D/g, '')}?text=${waMsg}`}
                          target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 font-medium">
                          <MessageCircle size={12} aria-hidden /> WhatsApp
                        </a>
                      )}
                      {r.email && (
                        <a href={`mailto:${r.email}`}
                          className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 truncate max-w-[160px]">
                          <Mail size={12} aria-hidden /> {r.email}
                        </a>
                      )}
                    </div>

                    {/* Acciones */}
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          await reservasApi.confirmar(r.id).catch(() => {});
                          setRecarga(x => x + 1);
                          refetchVencer();
                        }}
                        className="flex-1 text-xs py-2 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 inline-flex items-center justify-center gap-1"
                      >
                        <Check size={13} aria-hidden /> Confirmar
                      </button>
                      <button
                        onClick={async () => {
                          if (!confirm(`¿Cancelar la reserva de ${r.nombreCliente}?`)) return;
                          await reservasApi.cancelar(r.id, "Cancelada por el establecimiento").catch(() => {});
                          setRecarga(x => x + 1);
                          refetchVencer();
                        }}
                        className="flex-1 text-xs py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 inline-flex items-center justify-center gap-1"
                      >
                        <XIcon size={13} aria-hidden /> Cancelar
                      </button>
                    </div>
                  </div>
                );
              })}
              {(porVencerData?.count ?? 0) === 0 && (
                <div className="py-16 text-center text-slate-400">
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-50 text-emerald-400 mb-3" aria-hidden>
                    <CheckCircle2 size={36} strokeWidth={1.7} />
                  </span>
                  <p className="text-sm">No hay reservas por vencer en las próximas 24 horas.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
